import React, { useMemo } from 'react';

import _ from 'lodash';

import { gql } from '@nerdwallet/apollo';

import {
  FinancialBankingReward,
  FinancialCreditCardReward,
} from '@generated-gql/generated-types';

import RewardsTrackerList from '../rewards-tracker-list';
import RewardsTracker from '../rewards-tracker';
import InvestmentAccountRewardTracker from '../investment-account-tracker';
import { When } from 'react-if';

const NW_RICH_TEXT_FRAGMENT = gql`
  fragment NerdwalletPlusRichTextFragment on NwPlusRichText {
    text
    links {
      tag
      url
      type
      text
    }
  }
`;

const NW_PLUS_SHOPPING_REWARD_STEP = gql`
  fragment NerdwalletPlusShoppingRewardStepFragment on FinancialNwPlusRewardStep {
    alertMessage {
      ...NerdwalletPlusRichTextFragment
    }
    analyticsStatus
    ctaText {
      ...NerdwalletPlusRichTextFragment
    }
    extraTagline {
      ...NerdwalletPlusRichTextFragment
    }
    failedTagline {
      ...NerdwalletPlusRichTextFragment
    }
    infoMessage {
      ...NerdwalletPlusRichTextFragment
    }
    status
    tagline {
      ...NerdwalletPlusRichTextFragment
    }
    title {
      ...NerdwalletPlusRichTextFragment
    }
  }
`;

const NERDWALLET_PLUS_SHOPPING_REWARD_FRAGMENT = gql`
  fragment NerdwalletPlusShoppingRewardTrackerResponseFragment on FinancialNwPlusRewardResponse {
    creditCards {
      id
      shoppingFinishedAt
      accountLinkStatus
      nwPlusActivationStatus
      productType
      marketplaceOfferId
      marketplaceCreditCardOffer {
        displayName
        institution {
          name
          id
        }
      }
      events {
        id
        eventType
        emittedAt
        viewedAt
        pointsRewarded
      }
      steps(currentDate: $currentDate) {
        ...NerdwalletPlusShoppingRewardStepFragment
      }
      rewardsProgress(currentDate: $currentDate) {
        hasAppliedForCard
        hasConfirmedCardApproval
        hasExpiredErrorsToReport
        hasLinkedAccount
        hasMadeOnTimePayment
        hasCardApprovalConfirmationExpired
        hasOnTimePaymentRewardExpired
      }
    }
    bankingAccounts {
      accountId
      accountLinkStatus
      id
      events {
        id
        emittedAt
        viewedAt
        pointsRewarded
        eventType
      }
      steps {
        ...NerdwalletPlusShoppingRewardStepFragment
      }
      shoppingFinishedId
      shoppingFinishedAt
      productType
      marketplaceBankingOffer {
        displayName
        institution {
          name
          id
        }
      }
      marketplaceInstitutionId
      marketplaceProductId
      marketplaceOfferId
      nwPlusActivationStatus
      rewardEligibilityStatus
      rewardPathType
      accountLinkedAt
      rewardsProgress(currentDate: $currentDate) {
        hasAppliedForBankingAccount
        hasConfirmedBankingAccount
        hasLinkedAccount
        hasMadeDirectDeposit
        hasFundedAccount
        hasBankAccountConfirmationExpired
        hasDirectDepositRewardExpired
        hasFundedAccountRewardExpired
        hasExpiredErrorsToReport
      }
    }
  }

  ${NW_RICH_TEXT_FRAGMENT}
  ${NW_PLUS_SHOPPING_REWARD_STEP}
`;

export const NERDWALLET_PLUS_SHOPPING_REWARDS_QUERY = gql`
  query GetFinancialNerdWalletPlusShoppingRewards(
    $filter: FinancialNwPlusRewardFilterInput
    $currentDate: DateTime
  ) {
    financialNwPlusRewards(filter: $filter, currentDate: $currentDate) {
      ...NerdwalletPlusShoppingRewardTrackerResponseFragment
    }
  }
  ${NERDWALLET_PLUS_SHOPPING_REWARD_FRAGMENT}
`;

/**
 * This list of trackers will mix, credit cards, banking and investment account rewards in a horizontal list.
 */

const ShoppingRewardsTrackersWrapper = ({
  isFreemiumUser = false,
  nwPlusCreditCards,
  nwPlusBankingAccounts,
  nwPlusInvestmentAccounts,
  refetchRewards,
  refetchNwPlusInvestmentAccounts,
  showInvestmentAccountTrackerSection,
}): JSX.Element => {
  const shoppingRewards: (
    | FinancialCreditCardReward
    | FinancialBankingReward
  )[] = useMemo(() => {
    const allRewards: (FinancialCreditCardReward | FinancialBankingReward)[] = [
      ...nwPlusCreditCards,
      ...nwPlusBankingAccounts,
    ];
    // sort by oldest shopping event first
    return _.orderBy(allRewards, (obj) => new Date(obj.shoppingFinishedAt), [
      'asc',
    ]);
  }, [nwPlusBankingAccounts, nwPlusCreditCards]);

  if (!shoppingRewards?.length && !nwPlusInvestmentAccounts?.length)
    return null;

  return (
    <RewardsTrackerList>
      {showInvestmentAccountTrackerSection && (
        <When
          condition={
            showInvestmentAccountTrackerSection &&
            !!nwPlusInvestmentAccounts?.length
          }
        >
          {nwPlusInvestmentAccounts.map((reward, idx) => (
            <InvestmentAccountRewardTracker
              key={idx}
              investmentAccountReward={reward}
              isFreemiumUser={isFreemiumUser}
              refetchNwPlusInvestmentAccounts={refetchNwPlusInvestmentAccounts}
            />
          ))}
        </When>
      )}
      {shoppingRewards.map((shoppingReward) => (
        <RewardsTracker
          key={shoppingReward.id}
          reward={shoppingReward}
          refetchRewards={refetchRewards}
        />
      ))}
    </RewardsTrackerList>
  );
};

export default ShoppingRewardsTrackersWrapper;
